import { useState, useEffect, useCallback } from "react";
import { PlateBlock } from "../../components/PlateBlock";
import styled from "styled-components";
import { HASH_ROUTES } from "../../constants/routes";
import { AnimateIn } from "../../components/AnimateIn";

const Wrap = styled.div`
  width: 100%;
  position: relative;
`;

const SliderContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const SliderTrack = styled.div<{ $offset: number }>`
  display: flex;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(-${({ $offset }) => $offset * 100}%);
  padding-bottom: 100px;
  padding-top: 20px;

  > * {
    flex-shrink: 0;
    width: 100%;
  }
`;

const Controls = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 48px;
  z-index: 10;

  @media screen and (max-width: 768px) {
    bottom: 32px;
    gap: 32px;
  }

  @media screen and (max-width: 440px) {
    bottom: 24px;
    gap: 24px;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 14px;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? "32px" : "12px")};
  height: 12px;
  border-radius: 6px;
  border: none;
  background: ${({ $active }) =>
    $active ? "rgba(0, 0, 0, 0.18)" : "rgba(0, 0, 0, 0.1)"};
  cursor: pointer;
  transition: all 0.4s ease;
  padding: 0;

  &:hover {
    background: ${({ $active }) =>
      $active ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.5)"};
  }
`;

const NavigationControls = styled.div`
  display: flex;
  gap: 12px;
`;

const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(12px);

  i {
    font-size: 18px;
    color: #ffffff;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.18);
    border-color: rgba(255, 255, 255, 0.35);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }

  @media screen and (max-width: 768px) {
    width: 48px;
    height: 48px;

    i {
      font-size: 16px;
    }
  }
`;

export const Plates = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const totalSlides = 5;

  const next = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, []);

  const previous = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      next();
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, next]);

  const handleSlideClick = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleNavClick = (direction: "prev" | "next") => {
    if (direction === "prev") previous();
    else next();
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <AnimateIn msDelay={0}>
      <Wrap id={HASH_ROUTES.metrix}>
        <SliderContainer>
          <SliderTrack $offset={currentSlide}>
            <PlateBlock step={1} />
            <PlateBlock step={2} />
            <PlateBlock step={3} />
            <PlateBlock step={4} />
            <PlateBlock step={5} />
          </SliderTrack>
        </SliderContainer>

        <Controls>
          <DotsContainer>
            {[0, 1, 2, 3, 4].map((index) => (
              <Dot
                key={index}
                $active={currentSlide === index}
                onClick={() => handleSlideClick(index)}
              />
            ))}
          </DotsContainer>

          <NavigationControls>
            <ArrowButton onClick={() => handleNavClick("prev")}>
              <i className="pi pi-chevron-left" />
            </ArrowButton>
            <ArrowButton onClick={() => handleNavClick("next")}>
              <i className="pi pi-chevron-right" />
            </ArrowButton>
          </NavigationControls>
        </Controls>
      </Wrap>
    </AnimateIn>
  );
};
